/**
 * Shared utility functions for batch payment forms
 * Used by both BatchManualPaymentDetailsForm and BatchPaymentDetailsForm
 */

import { TBatchPaymentDetail } from '@/features/payments/types/BatchPayment';

/**
 * Batch payment configuration constants
 */
export const BatchPaymentConfig = {
  BATCH_NAME_LIMIT: 12,
  MAX_PAYMENTS: 100,
  ALLOWED_WALLET_TYPE: 'everyday'
} as const;

export const VALID_CURRENCY_AMOUNT_REGEX = /^(?:\d+(?:\.\d{1,2})?|\.\d{1,2})$/;

/**
 * Enum representing the supported fields in a batch payment detail.
 * Used for mapping UI fields to backend payload structure.
 */
export enum PayeeFieldEnum {
  NAME = 'payeeName',
  BANK_ACCOUNT_NUMBER = 'bankAccountNumber',
  AMOUNT = 'amount',
  PARTICULARS = 'particulars',
  CODE = 'code',
  REFERENCE = 'reference'
}

/**
 * Truncates a field value to maximum 12 characters
 */
export const truncateField = (value?: string): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 12 ? trimmed.substring(0, 12) : trimmed;
};

/**
 * Creates an empty payment detail object with all required fields
 */
export const createEmptyPaymentDetail = (): TBatchPaymentDetail => ({
  payeeName: '',
  bankAccountNumber: '',
  amount: '',
  particulars: undefined,
  code: undefined,
  reference: undefined,
  payeeVerified: false
});

/**
 * Handles field change for payment details with truncation for specific fields
 */
export const handleFieldChangeHelper = (
  currentDetails: TBatchPaymentDetail[],
  index: number,
  field: keyof TBatchPaymentDetail,
  value: string
): TBatchPaymentDetail[] => {
  const updated = [...currentDetails];
  if (field === 'particulars' || field === 'code' || field === 'reference') {
    (updated[index][field] as string | undefined) = truncateField(value);
  } else {
    (updated[index][field] as string) = value;
  }
  return updated;
};

/**
 * Parses an amount string that may contain currency symbols, thousand
 * separators, or whitespace (e.g. "$16,000.00") into a number - returning NaN
 * if what's left after stripping those isn't a valid plain numeric amount
 * (e.g. "16,.005.00" or "16,00a6.00"), instead of parseFloat's default behavior
 * of silently reading only the leading valid portion.
 */
export const validateAndParseAmount = (value: string): number => {
  const formattedAmount = value.replace(/[$,\s]/g, '');

  if (!VALID_CURRENCY_AMOUNT_REGEX.test(formattedAmount)) {
    return NaN;
  }

  const amount = parseFloat(formattedAmount);

  return Number.isFinite(amount) ? amount : NaN;
};

/**
 * Calculates total amount from payment details
 */
export const calculateTotalAmount = (paymentDetails: TBatchPaymentDetail[]): number => {
  return paymentDetails.reduce((sum, detail) => {
    const amount = validateAndParseAmount(detail.amount) || 0;
    return sum + amount;
  }, 0);
};
